"use client";

import React from "react";
import { Breadcrumb as AntBreadcrumb } from "antd";
import { HomeOutlined, RightOutlined } from "@ant-design/icons";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/context/AuthContext";
import { getFirstAvailablePage } from "@/utils/common";

export const SimpleBreadcrumb: React.FC = () => {
  const pathname = usePathname();
  const { user } = useAuth();

  // Pages where breadcrumb should be hidden
  const hiddenPages = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/resend-verification",
    "/dashboard",
  ];

  if (hiddenPages.includes(pathname)) {
    return null;
  }

  // Route mapping for better breadcrumb labels
  const routeLabels: Record<string, string> = {
    admin: "Admin",
    users: "Users",
    roles: "Roles",
    "row-level-settings": "Row Level Settings",
    features: "Features",
    permissions: "Permissions",
    "master-report": "Master Report",
    "budget-pacing": "Budget Pacing",
    "amber-alert": "Amber Alerts",
    dashboard: "Dashboard",
    profile: "Profile",
    docs: "Documentation",
  };

  // Generate breadcrumbs from pathname
  const generateBreadcrumbs = () => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const breadcrumbs: Array<{
      label: string;
      href?: string;
      icon?: React.ReactNode;
    }> = [];

    // Determine the correct home page based on user permissions
    const homePage = getFirstAvailablePage(user);

    // Always start with Home
    breadcrumbs.push({
      label: "Home",
      href: homePage,
      icon: <HomeOutlined style={{ marginRight: 5 }} />,
    });

    // Add all path segments as non-clickable breadcrumbs
    pathSegments.forEach((segment) => {
      // Use route mapping or convert segment to readable label
      const label =
        routeLabels[segment] ||
        segment
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

      breadcrumbs.push({
        label,
        href: undefined, // Make all segments non-clickable
        icon: undefined,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = generateBreadcrumbs();

  return (
    <AntBreadcrumb
      separator={<RightOutlined className="text-gray-400" />}
      className="mb-4"
    >
      {breadcrumbItems.map((item, index) => {
        const isLast = index === breadcrumbItems.length - 1;
        const isHome = index === 0; // Only the first item (Home) should be clickable

        if (isHome && item.href) {
          // Home item - clickable with client-side navigation
          return (
            <AntBreadcrumb.Item key={index}>
              <Link
                href={item.href}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                {item.icon}
                {item.label}
              </Link>
            </AntBreadcrumb.Item>
          );
        }

        // All other items - non-clickable
        return (
          <AntBreadcrumb.Item key={index}>
            <span
              className={`flex items-center gap-2 ${
                isLast ? "text-gray-900 font-medium" : "text-gray-600"
              }`}
            >
              {item.icon}
              {item.label}
            </span>
          </AntBreadcrumb.Item>
        );
      })}
    </AntBreadcrumb>
  );
};
